import React from 'react';
import Responsive from '@amzn/meridian/responsive';
import { responsiveProps } from 'src/constants/responsiveProps';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Alert from '@amzn/meridian/alert';
import { ContainerDetailsModel, ContainerType } from 'src/components/common/container-details/ContainerDetailsModel';
import Button from '@amzn/meridian/button';
import { getContainerIdString } from 'src/data-map/containerDetailsToStringID';
import {
    buildGetContainerDetailsInput,
    GetContainerDetailsInput,
    GetContainerDetailsOutput,
    GetContainerMode
} from 'src/models/GetContainerDetails';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import {
    getChildrenType,
    getChildrenCount,
    getGrandChildrenCount,
    getGrandChildrenType,
    containsInventoryItem
} from 'src/utils/container-details-util';
import '../../Search.scss';

interface SearchedContainerDetailsProps {
    renderForChildContainers: boolean;
}

interface SearchedContainerDetailsStateProps {
    containerDetails?: GetContainerDetailsOutput;
    warehouseId: string;
    associate: string;
    locale: string;
}

interface SearchedContainerDetailsDispatchProps {
    fetchContainerDetails: (getContainerDetailsInput: GetContainerDetailsInput) => void;
}

type Props = SearchedContainerDetailsProps & SearchedContainerDetailsStateProps & SearchedContainerDetailsDispatchProps;

class SearchedContainerDetailsTable extends React.Component<Props> {

    private onChildContainerSubmit = async (containerId: string) => {
        await this.props.fetchContainerDetails(buildGetContainerDetailsInput(containerId, this.props.warehouseId,
            this.props.associate, this.props.locale, GetContainerMode.SEARCH, true));
    }

    private properties = [
        { key: 'sortationCategories', label: AppStrings.SEARCH_TABLE_SORTATION_CATEGORY },
        { key: 'containerProperties.cleanDecantDate', label: AppStrings.SEARCH_TABLE_CDD },
        { key: 'latestProcessingDate', label: AppStrings.SEARCH_TABLE_LPD },
        { key: 'sla', label: AppStrings.SEARCH_TABLE_SLA },
        { key: 'containerProperties.RMAId', label: AppStrings.SEARCH_TABLE_RMA_ID },
        { key: 'containerProperties.Asin', label: AppStrings.SEARCH_TABLE_ASIN },
        { key: 'containerProperties.cleanDecantSortRuleId', label: AppStrings.SEARCH_TABLE_RULE_ID },
        { key: 'containerProperties.SellerType', label: AppStrings.SEARCH_TABLE_SELLER_TYPE },
        { key: 'containerProperties.TTSorTTA', label: AppStrings.SEARCH_TABLE_TTS_OR_TTA },
        { key: 'containerProperties.requiredHazmatLabels', label: AppStrings.SEARCH_TABLE_HAZMAT_LABELS },
        { key: 'latestContainerOperation.name', label: AppStrings.SEARCH_TABLE_LATEST_CONTAINER_OPERATION_NAME },
        { key: 'latestContainerOperation.time', label: AppStrings.SEARCH_TABLE_LATEST_CONTAINER_OPERATION_TIME },
        { key: 'latestContainerOperation.associate', label: AppStrings.SEARCH_TABLE_LATEST_CONTAINER_OPERATION_ASSOCIATE }
    ];

    private getTableRows = (containers: ContainerDetailsModel[]) => {
        return containers.map((container, i) => {
            const childrenType = getChildrenType(container);
            const childrenCount = getChildrenCount(container);
            const grandChildrenType = this.props.renderForChildContainers ? undefined : getGrandChildrenType(this.props.containerDetails);
            const grandChildrenCount = this.props.renderForChildContainers ? undefined : getGrandChildrenCount(this.props.containerDetails);
            return (
                <TableRow key={i} backgroundColor={this.props.renderForChildContainers ? "secondary" : "primary"}>
                    {container.containerType &&
                        <TableCell>
                            <Button type={"link"} size={"small"}
                                    onClick={() => this.onChildContainerSubmit(container.containerId)}>
                                {container.containerId}
                            </Button>
                        </TableCell>
                    }
                    {childrenType && <TableCell>{childrenCount}</TableCell>}
                    {grandChildrenType && <TableCell>{grandChildrenCount}</TableCell>}
                    {this.renderAdditionalProperties(container, containers)}
                </TableRow>
            )
        });
    }

    private renderAdditionalProperties = (container: ContainerDetailsModel, containers: ContainerDetailsModel[]) => {
        /* For all the properties, check if the container has that property inside it. If yes, render its value.
           If no, then check if any of the containers has a value against that property. If yes, render empty cell. */
        return this.properties.map(({ key, label }) => {
            const keys = key.split('.');
            const value = keys.reduce((acc: any, cur) => acc?.[cur], container);
            if (value) {
                if (key === "sortationCategories" && Array.isArray(value)) {
                    return <TableCell key={key}>{value.join(', ')}</TableCell>;
                }
                return <TableCell key={key}>{String(value)}</TableCell>;
            } else {
                const existsInChild = containers.some(container =>
                    keys.reduce((acc: any, cur) => acc?.[cur], container)
                );
                return existsInChild ? <TableCell key={key}>{}</TableCell> : null;
            }
        });
    }

    private getHeaderRow = (containers: ContainerDetailsModel[]) => {
        const childrenType: ContainerType|undefined = getChildrenType(containers?.[0])
        const grandChildrenType: ContainerType|undefined = this.props.renderForChildContainers ? undefined :
            getGrandChildrenType(this.props.containerDetails);

        return (
            <TableRow backgroundColor={this.props.renderForChildContainers ? "secondary" : "primary"}>
                {containers[0]?.containerType &&
                    <TableCell>{getContainerIdString(containers[0].containerType)}</TableCell>}
                {childrenType && <TableCell>{childrenType}</TableCell>}
                {grandChildrenType && <TableCell>{grandChildrenType}</TableCell>}
                {this.renderHeaderProperties(containers)}
            </TableRow>
        );
    }

    private renderHeaderProperties = (containers: ContainerDetailsModel[]) => {
        /* If the key is present in at least one of containers, print that label */
        return this.properties.map(({key, label}) => (
            containers.some(container => key.split('.').reduce((acc: any, cur) => acc?.[cur], container))
            && <TableCell key={key}>{label}</TableCell>
        ));
    }

    public render() {
        if (!this.props.containerDetails) {
            return null;
        }

        /* We render two tables in Search page. One for the container that is searched, and the other one for its child containers.
           `renderForChildContainers` flag is to distinguish between the two.
        * */
        const {containerDetails, renderForChildContainers} = this.props;
        const containers = renderForChildContainers ? containerDetails.childContainers || [] : [containerDetails.container];

        return (
            <Responsive query={"max-width"} props={responsiveProps}>
                {props => (
                    <Table headerRows={renderForChildContainers ? 1 : 2} showDividers={true}
                           className={"searched-container-table"} spacing={"small"}
                           data-testid={renderForChildContainers ? "search-child-containers-table" : "search-results-table"}>
                        {!renderForChildContainers && <TableRow><TableCell
                            columnSpan={13}>{containerDetails.container.containerType} Information</TableCell></TableRow>}
                        {!renderForChildContainers && containsInventoryItem(containerDetails) && (
                            <TableRow>
                                <TableCell columnSpan={13}>
                                    <Alert type="warning" size="small">
                                        {AppStrings.CONTAINS_INVENTORY_ITEM}
                                    </Alert>
                                </TableCell>
                            </TableRow>
                        )}
                        {this.getHeaderRow(containers)}
                        {this.getTableRows(containers)}
                    </Table>
                )}
            </Responsive>
        );
    }
}

export default SearchedContainerDetailsTable;
